import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore - react-dom types not available  
import { createPortal } from 'react-dom';
import { type PopupData } from '../services/popupService';

interface PopupModalProps {
  popup: PopupData;
  tenantId: string;
  onClose: (popupId: string, dontShowAgain?: boolean) => void;
}

const PopupModal: React.FC<PopupModalProps> = ({ popup, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const autoCloseTimeout = (popup as any).autoCloseTimeout || 0;
  const shouldAutoClose = autoCloseTimeout > 0;
  const isForceShow = popup.isForceShow || false;
  const imagePosition = popup.imagePosition || 'top';
  const imageFit = popup.imageStyle === 'auto' ? 'contain' : popup.imageStyle || 'cover';



  // Clamp any images or rich content inside popup.content to modal width
  const contentRef = useRef<HTMLDivElement | null>(null);
  const getPlainText = (html?: string): string => {
    if (!html) return '';
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };
  const getRenderedHtml = (html?: string): string => {
    if (!html) return '';
    return /<[^>]+>/.test(html) ? html : html.replace(/\n/g, '<br />');
  };
  const displayMode = popup.displayMode || 'auto';
  const rawHasTitleText = Boolean((popup.title || '').trim());
  const rawHasContentText = Boolean(getPlainText(popup.content));
  const hasImage = Boolean(popup.imageUrl);
  const renderedContentHtml = getRenderedHtml(popup.content);
  const shouldRenderImage = hasImage && displayMode !== 'text_only';
  const shouldRenderText = displayMode === 'image_only' ? false : (rawHasTitleText || rawHasContentText);
  const hasTitleText = shouldRenderText && rawHasTitleText;
  const hasContentText = shouldRenderText && rawHasContentText;
  const hasTextualContent = hasTitleText || hasContentText;
  const topImageMaxHeight = hasTextualContent ? 'min(32vh, 240px)' : '70vh';
  const bottomImageMaxHeight = hasTextualContent ? 'min(28vh, 220px)' : '45vh';
  const sideImageMaxHeight = hasTextualContent ? 'min(34vh, 280px)' : '360px';
  const centerImageMaxHeight = hasContentText ? 'min(26vh, 210px)' : '260px';
  const closeButtonTone: 'light' | 'dark' =
    shouldRenderImage && (!hasTextualContent || imagePosition === 'top' || imagePosition === 'background')
      ? 'dark'
      : 'light';
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const mediaElements = container.querySelectorAll<HTMLElement>('img, video, iframe');
    mediaElements.forEach((el) => {
      el.style.maxWidth = '100%';
      el.style.height = 'auto';
      el.style.display = 'block';
      el.style.borderRadius = '12px';
    });
  }, [popup.content]);

  useEffect(() => {
    // Show animation
    const showTimer = setTimeout(() => setIsVisible(true), 100);

    // Auto-close timer
    if (shouldAutoClose) {
      setTimeRemaining(Math.ceil(autoCloseTimeout / 1000));
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev && prev <= 1) {
            handleClose();
            return null;
          }
          return prev ? prev - 1 : null;
        });
      }, 1000);

      return () => {
        clearTimeout(showTimer);
        clearInterval(interval);
      };
    }

    return () => clearTimeout(showTimer);
  }, [shouldAutoClose, autoCloseTimeout]);

  const handleClose = async () => {
    setIsVisible(false);
    
    // Wait for animation to complete
    setTimeout(() => {
      onClose(popup._id, dontShowAgain);
    }, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && popup.allowDismiss) {
      handleClose();
    }
  };

  const renderTimer = (light: boolean) => {
    if (!shouldAutoClose || timeRemaining === null) return null;
    return (
      <div
        className="mt-4 p-3 bdr-5"
        style={{
          backgroundColor: light ? '#f0fdf4' : 'rgba(255,255,255,0.15)',
          color: light ? '#004c3d' : '#ffffff',
          border: light ? '1px solid #00a16f' : '1px solid rgba(255,255,255,0.2)'
        }}
      >
        <div className="text-xs text-center">
          <i className="fas fa-clock me-1"></i>
          ปิดอัตโนมัติใน {timeRemaining} วินาที
        </div>
        <div className="mt-2 w-full rounded-full h-1 overflow-hidden" style={{ backgroundColor: light ? '#d1fae5' : 'rgba(255,255,255,0.3)' }}>
          <div
            className="h-1 transition-all duration-1000"
            style={{
              width: `${(timeRemaining / (autoCloseTimeout / 1000)) * 100}%`,
              backgroundColor: light ? '#00a16f' : '#ffffff'
            }}
          />
        </div>
      </div>
    );
  };

  const renderDismissToggle = (light: boolean) => {
    if (!popup.allowDismiss || isForceShow) return null;
    return (
      <div
        style={{
          marginTop: '20px',
          display: light ? 'flex' : 'block',
          justifyContent: light ? 'center' : undefined
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            borderRadius: '14px',
            border: `1px solid ${light ? '#cbd5e1' : 'rgba(255,255,255,0.24)'}`,
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.2,
            boxShadow: light ? '0 8px 18px rgba(15,23,42,0.08)' : '0 10px 22px rgba(2,6,23,0.18)',
            color: light ? '#475569' : '#ffffff',
            background: light ? 'rgba(248,250,252,0.96)' : 'rgba(255,255,255,0.08)',
            userSelect: 'none'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              width: '16px',
              height: '16px',
              flex: '0 0 16px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '5px',
              border: `1.5px solid ${dontShowAgain
                ? (light ? '#00a16f' : '#ffffff')
                : (light ? '#64748b' : 'rgba(255,255,255,0.72)')}`,
              transition: 'all 160ms ease',
              background: dontShowAgain ? (light ? '#00a16f' : '#ffffff') : 'transparent',
              color: dontShowAgain ? (light ? '#ffffff' : '#004c3d') : 'transparent',
              fontSize: '10px',
              fontWeight: 700,
              boxShadow: dontShowAgain ? '0 2px 8px rgba(0,161,111,0.22)' : 'none'
            }}
          >
            ✓
          </span>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: '1px',
              height: '1px'
            }}
          />
          <div style={{ whiteSpace: 'nowrap' }}>ไม่ต้องแสดงอีก</div>
        </label>
      </div>
    );
  };

  const renderCloseButton = (tone: 'light' | 'dark' = 'dark') => (
    <button
      type="button"
      aria-label="Close"
      onClick={() => handleClose()}
      style={{
        display: 'flex',
        width: '36px',
        height: '36px',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: '12px',
        right: '12px',
        zIndex: 3,
        borderRadius: '999px',
        borderWidth: '1px',
        borderStyle: 'solid',
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.25)',
        color: tone === 'dark' ? '#ffffff' : '#0f172a',
        borderColor: tone === 'dark' ? 'rgba(255,255,255,0.78)' : 'rgba(15,23,42,0.14)',
        background: tone === 'dark' ? 'rgba(0,76,61,0.76)' : 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(10px)'
      }}
    >
      ×
    </button>
  );

  const renderHtmlContent = (light: boolean, centered: boolean = false) => (
    <div style={{ textAlign: centered ? 'center' : 'left' }}>
      {hasTitleText && (
        <h5
          className="t-medium"
          style={{
            color: light ? '#00a16f' : '#ffffff',
            fontSize: '30px',
            fontWeight: 700,
            marginBottom: '12px',
            textAlign: centered ? 'center' : 'left'
          }}
        >
          {popup.title}
        </h5>
      )}
      {hasContentText && (
        <div
          ref={contentRef}
          className={light ? 'text-gray-700' : 'c-white'}
          style={{
            fontSize: '15px',
            lineHeight: 1.8,
            textAlign: centered ? 'center' : 'left'
          }}
          dangerouslySetInnerHTML={{ __html: renderedContentHtml }}
        />
      )}
      {renderTimer(light)}
      {renderDismissToggle(light)}
    </div>
  );

  const renderImage = (styleOverrides: React.CSSProperties = {}) => (
    <div style={{ position: 'relative' }}>
      <img
        src={popup.imageUrl || ''}
        alt={popup.imageAlt || popup.title || 'modal content'}
        style={{
          width: '100%',
          maxWidth: '100%',
          objectFit: imageFit as React.CSSProperties['objectFit'],
          display: 'block',
          ...styleOverrides
        }}
      />
    </div>
  );

  const renderBody = () => {
    if (shouldRenderImage && !hasTextualContent) {
      return renderImage({ maxHeight: '70vh', borderRadius: '18px' });
    }

    if (!shouldRenderImage) {
      return <div className="p-6">{renderHtmlContent(true)}</div>;
    }

    if (imagePosition === 'background') {
      return (
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '420px',
            maxHeight: 'calc(78vh - 24px)',
            backgroundImage: `linear-gradient(rgba(0,76,61,0.6), rgba(0,76,61,0.82)), url("${popup.imageUrl}")`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: imageFit === 'fill' ? '100% 100%' : imageFit
          }}
        >
          <div style={{ position: 'relative', zIndex: 10, padding: '24px' }}>
            {renderHtmlContent(false, true)}
          </div>
        </div>
      );
    }

    if (imagePosition === 'left' || imagePosition === 'right') {
      return (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: imagePosition === 'right' ? 'row-reverse' : 'row'
          }}
        >
          <div style={{ flex: '0 0 40%', minWidth: '260px' }}>
            {renderImage({ height: '100%', minHeight: '220px', maxHeight: sideImageMaxHeight })}
          </div>
          <div style={{ flex: '1 1 300px', padding: '24px' }}>
            {renderHtmlContent(true)}
          </div>
        </div>
      );
    }

    if (imagePosition === 'bottom') {
      return (
        <>
          <div style={{ padding: '24px' }}>{renderHtmlContent(true)}</div>
          {renderImage({ maxHeight: bottomImageMaxHeight })}
        </>
      );
    }

    if (imagePosition === 'center') {
      return (
        <div style={{ padding: '24px' }}>
          {hasTitleText && (
            <h5
              className="t-medium"
              style={{ color: '#00a16f', fontSize: '30px', fontWeight: 700, marginBottom: '16px', textAlign: 'center' }}
            >
              {popup.title}
            </h5>
          )}
          {renderImage(
            {
              maxHeight: centerImageMaxHeight,
              width: imageFit === 'contain' ? 'auto' : '100%',
              margin: '0 auto 20px',
              borderRadius: '16px'
            }
          )}
          {hasContentText && (
            <div
              ref={contentRef}
              className="text-gray-700"
              style={{ fontSize: '15px', lineHeight: 1.8, textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: renderedContentHtml }}
            />
          )}
          {renderTimer(true)}
          {renderDismissToggle(true)}
        </div>
      );
    }

    return (
      <>
        {renderImage({ maxHeight: topImageMaxHeight })}
        <div className="bgg-1" style={{ padding: '24px' }}>{renderHtmlContent(false, true)}</div>
      </>
    );
  };

  return createPortal(
    <div
      className={`
        fixed inset-0 flex items-start justify-center
        transition-all duration-300 ease-out
        ${isVisible ? 'opacity-100' : 'opacity-0'}
        md:items-center
      `}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(2px)',
        paddingTop: '40px',
        paddingBottom: '40px',
        overflowY: 'auto'
      }}
    >
      <div
        className={`
          relative bgc-4 bdr-10 bx-sd
          transform transition-all duration-300 ease-out
          ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-8'}
          mx-auto flex flex-col
          mt-4 md:mt-0
        `}
        style={{
          maxWidth:
            shouldRenderImage && hasTextualContent && (imagePosition === 'left' || imagePosition === 'right' || imagePosition === 'background')
              ? '760px'
              : shouldRenderImage && !hasTextualContent
                ? 'min(92vw, 640px)'
                : shouldRenderImage
                  ? '520px'
                  : '420px',
          width: '90%',
          maxHeight: 'calc(90vh - 80px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
          zIndex: 1060,
          border: '2px solid #00a16f',
          position: 'relative',
          overflow: 'hidden',
          margin: '0 auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {popup.allowDismiss ? renderCloseButton(closeButtonTone) : null}

        {/* Modal Content */}
        <div className="modal-body p-0 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 20px)' }}>
          {renderBody()}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PopupModal;